import { FC, useEffect, useState } from 'react'
import { analytics } from '../../../utils/analytics'

interface AudioControlProps {
  audioRef: React.RefObject<HTMLAudioElement | null>
  isWatch?: boolean
}

const AudioControl: FC<AudioControlProps> = ({ audioRef, isWatch }) => {
  const [volume, setVolume] = useState(100)
  const [muted, setMuted] = useState(false)

  useEffect(() => {
    if (audioRef.current) {
      setMuted(audioRef.current.muted)
      setVolume(audioRef.current.muted ? 0 : audioRef.current.volume * 100)
    }
  }, [audioRef?.current])

  // Listen for sync events from PiP window
  useEffect(() => {
    const handleAudioControlSync = (event: Event) => {
      const customEvent = event as CustomEvent<{
        volume: number
        muted: boolean
      }>
      const { volume: syncVolume, muted: syncMuted } = customEvent.detail
      console.log('🔊 AudioControl received sync event', {
        syncVolume,
        syncMuted,
      })

      setVolume(syncMuted ? 0 : syncVolume * 100)
      setMuted(syncMuted)
    }

    window.addEventListener('audioControlSync', handleAudioControlSync)

    return () => {
      window.removeEventListener('audioControlSync', handleAudioControlSync)
    }
  }, [])

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)

    if (audioRef.current) {
      audioRef.current.volume = value
      if (audioRef.current.muted) {
        audioRef.current.muted = false
      }
      setVolume(value * 100)
      setMuted(false)

      // Notify PiP window about volume change
      console.log('🔊 Main AudioControl volume changed, notifying PiP', {
        value,
      })
    }
  }

  const handleMuteToggle = () => {
    if (!audioRef.current) return

    analytics.logEvent(
      isWatch
        ? 'watch_session_volume_btn_clicked'
        : 'regular_session_volume_btn_clicked',
    )

    if (audioRef.current.volume === 0) {
      audioRef.current.volume = 1
      setVolume(100)
    } else {
      audioRef.current.muted = !audioRef.current.muted
      setMuted(audioRef.current.muted)
      setVolume(audioRef.current.muted ? 0 : audioRef.current.volume * 100)
    }

    // Notify PiP window about mute change
    console.log('🔊 Main AudioControl mute toggled, notifying PiP', {
      muted: audioRef.current.muted,
      volume: audioRef.current.volume,
    })
  }

  return (
    <div className="relative tc-mv-volume-wrapper hidden md:block">
      <div className="absolute flex justify-center bottom-full left-0 right-0 rounded-full pb-2">
        <div className="w-8 flex items-center justify-center rounded-full tc-mv-volume p-2">
          <input
            type="range"
            className="w-2 rounded-md h-full appearance-none bg-transparent cursor-pointer tc-mv-volume-slider"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume / 100}
            onChange={handleVolumeChange}
            style={{
              background: `linear-gradient(to top, white 0%, white ${volume}%, rgba(0,0,0,0.4) ${volume}%, rgba(0,0,0,0.4) 100%)`,
            }}
          />
        </div>
      </div>
      <button
        className="tc-waveform-icon w-[40px] align-middle text-white"
        onClick={handleMuteToggle}
      >
        {muted || volume === 0 ? (
          <i className="fa-solid fa-volume-xmark" />
        ) : (
          <i className="fa-solid fa-volume-high" />
        )}
      </button>
    </div>
  )
}

export { AudioControl }
